// Lightweight helper to push ecommerce events to dataLayer for Yandex Metrika
// The project initializes Yandex with `ecommerce: "dataLayer"`, so pushing
// objects under `window.dataLayer` will be picked up by the tag.

export type EcomProduct = {
  id?: string | number;
  name?: string;
  price?: number | string;
  quantity?: number;
  category?: string;
  sku?: string;
  brand?: string;
  position?: number;
  list?: string;
};

function getDataLayer() {
  // ensure dataLayer exists
  // eslint-disable-next-line no-unused-expressions
  (window as any).dataLayer = (window as any).dataLayer || [];
  return (window as any).dataLayer as any[];
}

export function pushEcommerce(obj: any) {
  try {
    const dl = getDataLayer();
    dl.push({ ecommerce: obj });
    // also push a generic event so tag.js can react if needed
    dl.push({ event: 'ecommerce_event' });
  } catch (e) {
    // swallow
    // eslint-disable-next-line no-console
    console.debug('ecommerce push failed', e);
  }
}

export function pushAddToCart(product: EcomProduct) {
  pushEcommerce({ add: { products: [normalizeProduct(product)] } });
}

export function pushRemoveFromCart(product: EcomProduct) {
  pushEcommerce({ remove: { products: [normalizeProduct(product)] } });
}

export function pushBeginCheckout(products: EcomProduct[], step = 1) {
  pushEcommerce({ checkout: { actionField: { step }, products: products.map(normalizeProduct) } });
}

export function pushPurchase(orderId: string | number, revenue: number, products: EcomProduct[]) {
  pushEcommerce({ purchase: { actionField: { id: String(orderId), revenue: String(revenue) }, products: products.map(normalizeProduct) } });
}

// Push a list impressions (product listing) to dataLayer.
export function pushImpressions(products: EcomProduct[], listName?: string) {
  const normalized = products.map((p, idx) => ({ ...normalizeProduct(p), position: p.position ?? (idx + 1), list: p.list || listName }));
  pushEcommerce({ impressions: { products: normalized } });
}

// Push a product click event (user clicked on a product in a list)
export function pushProductClick(product: EcomProduct, listName?: string) {
  const normalized = { ...normalizeProduct(product), list: product.list || listName };
  pushEcommerce({ click: { actionField: { list: listName }, products: [normalized] } });
}

// Push product detail view (user opened product page)
export function pushViewProduct(product: EcomProduct) {
  pushEcommerce({ detail: { products: [normalizeProduct(product)] } });
}

function normalizeProduct(p: EcomProduct) {
  return {
    id: p.id !== undefined ? String(p.id) : undefined,
    name: p.name,
    price: p.price !== undefined ? String(p.price) : undefined,
    quantity: p.quantity !== undefined ? Number(p.quantity) : undefined,
    category: p.category,
    sku: p.sku,
    brand: p.brand,
    position: p.position,
    list: p.list,
  };
}

export default {
  pushAddToCart,
  pushRemoveFromCart,
  pushBeginCheckout,
  pushPurchase,
};
